import { useRef, useState } from 'react';
import { DeferredChoiceMenu as ChoiceMenu } from '~/ui/deferred/deferred-menus';
import { FormattedMessage, useIntl } from 'react-intl';
import { FilterTrigger } from './filter-trigger';
import { pick } from './pick';

export type CategoryOption = {
  id: string;
  name: string;
};

type CategoryFilterProps = {
  value: string[];
  options: CategoryOption[];
  onChange: (value: string[]) => void;
  /** Fired when the menu opens, for analytics. */
  onOpen?: () => void;
};

/**
 * Multi-select category (tag) filter for the home page search bar. Renders
 * the shared `<FilterTrigger>` (no leading icon) and anchors a Leaf
 * `ChoiceMenu`. Categories are sourced from the storefront catalog; an empty
 * `options` list still mounts the trigger so the bar stays visually
 * consistent across tenants — the menu just renders an empty state.
 *
 * Selections are committed via `onChange` on EVERY toggle (not on menu close)
 * — the route reflects the change instantly: cards re-filter client-side and
 * the upcoming-events carousel refetches (US-10172). `value` is the committed
 * source of truth (the URL), so the listbox reads straight from it.
 */
export function CategoryFilter({
  value,
  options,
  onChange,
  onOpen,
}: CategoryFilterProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const intl = useIntl();

  const selected = pick(options, value);

  return (
    <>
      <FilterTrigger
        ref={triggerRef}
        label={
          <FormattedMessage
            defaultMessage="Category"
            description="Label on the category filter button in the home page search bar"
          />
        }
        value={
          selected.length === 0 || selected.length === options.length ? (
            <FormattedMessage
              defaultMessage="All"
              description="Placeholder on the category filter button when no categories are selected"
            />
          ) : selected.length === 1 ? (
            selected[0]!.name
          ) : (
            <FormattedMessage
              defaultMessage="{count} categories"
              description="Summary on the category filter button when multiple categories are selected"
              values={{ count: selected.length }}
            />
          )
        }
        open={open}
        popupKind="listbox"
        onClick={() => {
          setOpen((wasOpen) => {
            if (!wasOpen) onOpen?.();
            return !wasOpen;
          });
        }}
      />

      <ChoiceMenu
        anchor={triggerRef}
        open={open}
        onOpenChange={setOpen}
        placement="bottom start"
        multiple
        value={value}
        onChange={onChange}
        items={options.map((o) => ({ value: o.id, label: o.name }))}
        aria-label={intl.formatMessage({
          defaultMessage: 'Select categories',
          description: 'Aria label for the home page category filter listbox',
        })}
      />
    </>
  );
}
